import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Loader2, Utensils } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import apiClient from '@/api/client'
import { StaffLoginPicker } from '@/components/admin/customers/CustomerHouseAccountsPanel'
import { parseAmountField } from '@/lib/houseAccounts'
import { getStoredUserId, getStoredUserRole } from '@/lib/staff-roles'
import { allowanceChangeBlock, allowanceLeft, mealsFromStatement, monthBounds, monthName } from '@/lib/staffAllowances'
import { toastHelpers } from '@/lib/toast-helpers'
import { cn, formatCurrency, formatDateHuman } from '@/lib/utils'
import type { HouseAccount, User } from '@/types'

export type StaffAllowanceTarget =
  | { mode: 'create-login'; user: User }
  | { mode: 'create-person' }
  | { mode: 'view'; accountId: string; cycleMonth: string }

/**
 * F-MM-N31 — give, change or stop one person's meal allowance from Manage
 * Staff, and see this month's meals. A person is a staff login or someone
 * without POS access. Only an admin changes an allowance, never their own;
 * everyone else sees it read-only.
 */
export function StaffAllowanceDialog({
  target,
  onClose,
  onChanged,
}: {
  target: StaffAllowanceTarget | null
  onClose: () => void
  onChanged: () => void
}) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-lg">
        {target?.mode === 'view' ? (
          <ViewAllowance accountId={target.accountId} cycleMonth={target.cycleMonth} onClose={onClose} onChanged={onChanged} />
        ) : target ? (
          <CreateAllowance target={target} onClose={onClose} onChanged={onChanged} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function staffName(u: Pick<User, 'first_name' | 'last_name' | 'username'>): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username
}

function CreateAllowance({
  target,
  onClose,
  onChanged,
}: {
  target: Exclude<StaffAllowanceTarget, { mode: 'view' }>
  onClose: () => void
  onChanged: () => void
}) {
  const forLogin = target.mode === 'create-login'
  const [amount, setAmount] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [touched, setTouched] = useState(false)
  const parsed = parseAmountField(amount)
  const nameMissing = !forLogin && name.trim() === ''

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiClient.createHouseAccount(
        forLogin
          ? { kind: 'staff_allowance', user_id: target.user.id, monthly_allowance: parsed.value ?? 0 }
          : {
              kind: 'staff_allowance',
              holder_name: name.trim(),
              holder_phone: phone.trim() || undefined,
              monthly_allowance: parsed.value ?? 0,
            },
      )
      if (!res.success) throw new Error(res.message || 'Could not give the allowance')
      return res.data
    },
    onSuccess: (acc) => {
      toastHelpers.success(`Meal allowance given to ${acc?.person_name ?? (forLogin ? staffName(target.user) : name.trim())}`)
      onChanged()
      onClose()
    },
    onError: (e: Error) => toastHelpers.error('Meal allowance', e.message),
  })

  const submit = () => {
    setTouched(true)
    if (parsed.value == null || nameMissing) return
    create.mutate()
  }

  return (
    <>
      <DialogHeader className="border-b px-5 py-4 text-left">
        <DialogTitle>{forLogin ? `Give ${staffName(target.user)} a meal allowance` : 'Add a person without POS access'}</DialogTitle>
        <DialogDescription>
          {forLogin
            ? 'They eat against it each month, with a manager PIN for every meal. Meals aren’t counted as sales.'
            : 'For staff who eat on an allowance but don’t use the POS, like a cleaner or helper. They can’t sign in.'}
        </DialogDescription>
      </DialogHeader>
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {!forLogin && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="allowance-person-name">Name</Label>
              <Input id="allowance-person-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
              {touched && nameMissing && <p className="text-xs text-destructive">Enter their name.</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="allowance-person-phone">
                Phone <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input id="allowance-person-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="0300 1234567" />
            </div>
          </>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="allowance-amount">Monthly allowance</Label>
          <Input
            id="allowance-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 6000"
            autoFocus={forLogin}
            aria-invalid={(touched && Boolean(parsed.error)) || undefined}
          />
          {touched && parsed.error ? (
            <p className="text-xs text-destructive">{parsed.error}</p>
          ) : (
            <p className="text-xs text-muted-foreground">Resets on the 1st. Anything over it is what they owe you.</p>
          )}
        </div>
        <button type="submit" hidden />
      </form>
      <DialogFooter className="border-t px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Give allowance'}
        </Button>
      </DialogFooter>
    </>
  )
}

type Editing = null | 'amount' | 'person' | 'link' | 'stop'

function ViewAllowance({
  accountId,
  cycleMonth,
  onClose,
  onChanged,
}: {
  accountId: string
  cycleMonth: string
  onClose: () => void
  onChanged: () => void
}) {
  const navigate = useNavigate()
  const range = monthBounds(cycleMonth)
  const statement = useQuery({
    queryKey: ['house-accounts', 'statement', accountId, range.from, range.to],
    queryFn: () => apiClient.getHouseAccountStatement(accountId, range),
  })
  const account: HouseAccount | undefined = statement.data?.data?.account
  const meals = mealsFromStatement(statement.data?.data?.entries ?? [])
  const block = account ? allowanceChangeBlock(getStoredUserRole(), getStoredUserId(), account.user_id) : null

  const [editing, setEditing] = useState<Editing>(null)
  const [amount, setAmount] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [linkId, setLinkId] = useState('')
  const [linkLabel, setLinkLabel] = useState('')
  const parsed = parseAmountField(amount)

  useEffect(() => {
    setEditing(null)
  }, [accountId])

  const update = useMutation({
    mutationFn: async (body: Parameters<typeof apiClient.updateHouseAccount>[1]) => {
      const res = await apiClient.updateHouseAccount(accountId, body)
      if (!res.success) throw new Error(res.message || 'Could not change the allowance')
      return res.data
    },
    onSuccess: (_acc, body) => {
      setEditing(null)
      onChanged()
      void statement.refetch()
      if (body.status === 'closed') {
        toastHelpers.success('Allowance stopped')
        onClose()
      }
    },
    onError: (e: Error) => toastHelpers.error('Meal allowance', e.message),
  })

  if (statement.isLoading || !account) {
    return (
      <div className="flex items-center gap-2 px-5 py-10 text-sm text-muted-foreground">
        <DialogTitle className="sr-only">Meal allowance</DialogTitle>
        <DialogDescription className="sr-only">Loading the allowance</DialogDescription>
        {statement.isError ? (
          'Could not load this allowance.'
        ) : (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
          </>
        )}
      </div>
    )
  }

  const { left, over } = allowanceLeft(account.monthly_allowance, account.allowance_used, account.overage_this_month)
  const pausedByDeactivation = account.status === 'suspended' && account.has_login && account.login_active === false
  const canChange = !block && account.status !== 'closed'
  const month = monthName(cycleMonth)

  return (
    <>
      <DialogHeader className="border-b px-5 py-4 text-left">
        <DialogTitle className="flex flex-wrap items-center gap-2">
          <Utensils className="h-4 w-4 text-muted-foreground" aria-hidden />
          {account.person_name}
          {!account.has_login && <Badge variant="outline">No POS access</Badge>}
          {account.status === 'suspended' && <Badge variant="secondary">Paused</Badge>}
        </DialogTitle>
        <DialogDescription>
          {account.has_login
            ? 'Meal allowance'
            : [account.holder_phone, 'Meal allowance'].filter(Boolean).join(' — ')}
        </DialogDescription>
      </DialogHeader>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <div className="grid grid-cols-3 gap-3 rounded-lg border p-3 text-sm">
          <Fact label="Each month" value={formatCurrency(account.monthly_allowance)} />
          <Fact label={`Used in ${month}`} value={formatCurrency(account.allowance_used + over)} />
          {over > 0 ? (
            <Fact label="Over (they owe)" value={formatCurrency(over)} tone="warning" />
          ) : (
            <Fact label="Left" value={formatCurrency(left)} />
          )}
        </div>

        {over > 0 && account.allowance_used + over < account.monthly_allowance && (
          <p className="text-sm text-muted-foreground">
            The {formatCurrency(over)} went over before the allowance was raised. What a meal owes is fixed when it’s charged.
          </p>
        )}
        {pausedByDeactivation && (
          <p className="text-sm text-muted-foreground">
            Paused because their login is deactivated. Reactivate them first, then restart it here.
          </p>
        )}
        {block && <p className="text-sm text-muted-foreground">{block}</p>}

        {editing === 'amount' ? (
          <InlineEdit
            onCancel={() => setEditing(null)}
            onSave={() => parsed.value != null && update.mutate({ monthly_allowance: parsed.value })}
            saving={update.isPending}
            error={parsed.error}
          >
            <Label htmlFor="allowance-edit-amount">Monthly allowance</Label>
            <Input id="allowance-edit-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
            <p className="text-xs text-muted-foreground">Applies to meals from now on. Meals already charged keep what they owe.</p>
          </InlineEdit>
        ) : editing === 'person' ? (
          <InlineEdit
            onCancel={() => setEditing(null)}
            onSave={() => name.trim() && update.mutate({ holder_name: name.trim(), holder_phone: phone.trim() })}
            saving={update.isPending}
            error={name.trim() ? null : 'Enter their name.'}
          >
            <Label htmlFor="allowance-edit-name">Name</Label>
            <Input id="allowance-edit-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
            <Label htmlFor="allowance-edit-phone">
              Phone <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input id="allowance-edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </InlineEdit>
        ) : editing === 'link' ? (
          <InlineEdit
            onCancel={() => setEditing(null)}
            onSave={() => linkId && update.mutate({ user_id: linkId })}
            saving={update.isPending}
            error={linkId ? null : 'Pick their staff login.'}
            saveLabel="Link"
          >
            <p className="text-sm text-muted-foreground">
              If {account.person_name} now has a POS login, link it. The allowance and its history move to them.
            </p>
            <StaffLoginPicker
              value={linkId}
              valueLabel={linkLabel}
              onChange={(id, label) => {
                setLinkId(id)
                setLinkLabel(label)
              }}
            />
          </InlineEdit>
        ) : editing === 'stop' ? (
          <div className="space-y-3 rounded-lg border border-destructive/40 p-3 text-sm">
            <p>
              Stop {account.person_name}’s allowance? Their past meals stay in Reports. To give them one again later, add a new
              allowance.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                Keep it
              </Button>
              <Button size="sm" variant="destructive" disabled={update.isPending} onClick={() => update.mutate({ status: 'closed' })}>
                Stop allowance
              </Button>
            </div>
          </div>
        ) : canChange ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setAmount(String(account.monthly_allowance))
                setEditing('amount')
              }}
            >
              Change amount
            </Button>
            {!account.has_login && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setName(account.holder_name ?? account.person_name)
                    setPhone(account.holder_phone ?? '')
                    setEditing('person')
                  }}
                >
                  Edit name or phone
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setLinkId('')
                    setLinkLabel('')
                    setEditing('link')
                  }}
                >
                  Link a staff login
                </Button>
              </>
            )}
            {account.status === 'active' ? (
              <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ status: 'suspended' })}>
                Pause
              </Button>
            ) : !pausedByDeactivation ? (
              <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ status: 'active' })}>
                Restart
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setEditing('stop')}>
              Stop allowance
            </Button>
          </div>
        ) : null}

        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Meals in {month}</h3>
          {meals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No meals yet this month.</p>
          ) : (
            <ul className="divide-y rounded-lg border text-sm">
              {meals.map((m) => (
                <li key={m.orderId} className={cn('flex items-center gap-3 px-3 py-2', m.undone && 'text-muted-foreground')}>
                  <div className="min-w-0 flex-1">
                    <div className={cn('truncate', m.undone && 'line-through')}>
                      {formatDateHuman(m.date)} · {m.orderNumber}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {m.undone ? 'Undone' : m.approvedBy ? `Approved by ${m.approvedBy}` : null}
                    </div>
                  </div>
                  <div className="text-right tabular-nums">
                    <div className={cn(m.undone && 'line-through')}>{formatCurrency(m.bill)}</div>
                    {!m.undone && m.over > 0 && <div className="text-xs text-warning-fg">{formatCurrency(m.over)} over</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <DialogFooter className="border-t px-5 py-3 sm:justify-between">
        <Button
          variant="ghost"
          onClick={() => {
            onClose()
            void navigate({ to: '/admin/reports', search: { tab: 'accounts' } })
          }}
        >
          All months in Reports
        </Button>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  )
}

function Fact({ label, value, tone }: { label: string; value: string; tone?: 'warning' }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div className={cn('font-semibold tabular-nums', tone === 'warning' && 'text-warning-fg')}>{value}</div>
    </div>
  )
}

function InlineEdit({
  children,
  onSave,
  onCancel,
  saving,
  error,
  saveLabel = 'Save',
}: {
  children: React.ReactNode
  onSave: () => void
  onCancel: () => void
  saving: boolean
  error: string | null
  saveLabel?: string
}) {
  const [tried, setTried] = useState(false)
  return (
    <form
      className="space-y-2 rounded-lg border p-3"
      onSubmit={(e) => {
        e.preventDefault()
        setTried(true)
        if (!error) onSave()
      }}
    >
      {children}
      {tried && error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? 'Saving…' : saveLabel}
        </Button>
      </div>
    </form>
  )
}
